import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL } from "@/lib/roles";
import { BookOpen, LogOut, ChevronUp, Check, Repeat } from "lucide-react";
import { toast } from "sonner";

// Role chip color tokens (Tailwind classes) keyed by role.
// Reused in both the active chip and the dropdown items.
const ROLE_CHIP_CLS = {
  super_admin:      "text-[#FACC15] border-[#FACC15] bg-[#FACC15]/10",
  admin:            "text-[#FACC15] border-[#FACC15] bg-[#FACC15]/10",
  admin_directive:  "text-[#22d3ee] border-[#22d3ee] bg-[#22d3ee]/10",
  admin_treasurer:  "text-[#34C759] border-[#34C759] bg-[#34C759]/10",
  coach:            "text-[#007AFF] border-[#007AFF] bg-[#007AFF]/10",
  player:           "text-[#A1A1AA] border-[#A1A1AA] bg-[#A1A1AA]/10",
  pending:          "text-[#FF9F0A] border-[#FF9F0A] bg-[#FF9F0A]/10",
};

const roleChip = (role) => ROLE_CHIP_CLS[role] || "text-white border-[#27272A]";

export default function ProfileMenu() {
  const { user, logout, switchRole, activeRole, primaryRole, authorizedRoles } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!user) return null;

  const canSwitch = (authorizedRoles || []).length > 1;

  const onSwitch = async (role) => {
    if (role === activeRole) { setOpen(false); return; }
    setSwitching(true);
    try {
      await switchRole(role);
      toast.success(`Ahora actuando como ${ROLE_LABEL[role] || role}`);
      setOpen(false);
      // Send user to dashboard to avoid landing on a page their new role can't see
      navigate("/dashboard");
    } catch {
      toast.error("No se pudo cambiar de rol");
    } finally {
      setSwitching(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="border-t border-[#27272A] p-4 relative" ref={wrapRef} data-testid="profile-menu">
      {/* Dropdown panel (opens upward) */}
      {open && (
        <div
          className="absolute bottom-full left-0 right-0 mx-4 mb-2 tactical-card border border-[#27272A] bg-[#0A0A0A] shadow-xl z-40 overflow-hidden"
          data-testid="profile-dropdown"
        >
          {canSwitch && (
            <>
              <div className="px-3 py-2 border-b border-[#27272A] flex items-center gap-2">
                <Repeat size={11} className="text-[#71717A]"/>
                <span className="overline text-[#71717A]">Cambiar de rol</span>
              </div>
              {authorizedRoles.map((r) => {
                const isActive = r === activeRole;
                const isPrimary = r === primaryRole;
                return (
                  <button
                    key={r}
                    type="button"
                    disabled={switching}
                    onClick={() => onSwitch(r)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-xs uppercase tracking-wider transition-colors ${
                      isActive ? "bg-[#1A1A1A]" : "hover:bg-[#121212]"
                    }`}
                    data-testid={`switch-role-${r}`}
                  >
                    <span className={`px-2 py-0.5 border text-[10px] font-bold tracking-widest ${roleChip(r)}`}>
                      {ROLE_LABEL[r] || r}
                    </span>
                    {isPrimary && (
                      <span className="text-[9px] text-[#71717A] uppercase tracking-widest">Principal</span>
                    )}
                    <span className="ml-auto">
                      {isActive && <Check size={14} className="text-[#34C759]"/>}
                    </span>
                  </button>
                );
              })}
              <div className="border-t border-[#27272A]"/>
            </>
          )}
          <a
            href="/manual.html"
            target="_blank"
            rel="noreferrer"
            data-testid="manual-link"
            className="flex items-center gap-2 px-3 py-2.5 text-xs uppercase tracking-wider text-[#A1A1AA] hover:bg-[#121212] hover:text-white"
            onClick={() => setOpen(false)}
          >
            <BookOpen size={12}/> Manual de usuario
          </a>
          <button
            type="button"
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2.5 text-xs uppercase tracking-wider text-[#A1A1AA] hover:bg-[#121212] hover:text-[#FF3B30]"
          >
            <LogOut size={12}/> Cerrar sesión
          </button>
        </div>
      )}

      {/* Trigger button: avatar + name + active role chip */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 text-left hover:bg-[#121212] transition-colors p-1 -m-1"
        data-testid="profile-menu-trigger"
        aria-expanded={open}
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-9 h-9 object-cover shrink-0" />
        ) : (
          <div className="w-9 h-9 bg-[#007AFF] flex items-center justify-center text-white font-bold shrink-0">
            {user.name?.[0]?.toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white truncate">{user.name}</div>
          <span
            className={`inline-block px-2 py-0.5 border text-[10px] font-bold tracking-widest mt-0.5 ${roleChip(activeRole)}`}
            data-testid="active-role-chip"
          >
            {ROLE_LABEL[activeRole] || activeRole}
          </span>
        </div>
        <ChevronUp
          size={14}
          className={`text-[#71717A] shrink-0 transition-transform ${open ? "" : "rotate-180"}`}
        />
      </button>
    </div>
  );
}
